import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TableSortLabel,
  Typography,
  Card,
} from "@mui/material";
import useWindowSize from "../../hooks/useWindowSize";
import { get } from "../../services/apiService";
import CircularLoader from "../../components/common/ScreenLoader";

// const sampleData = [
//   {
//     name: "Alhadid",
//     email: "al-hadid@example.com",
//     contact: "+9662865313",
//     plan: "Basic",
//     date: "2015-10-24",
//   },
//   {
//     name: "Manan",
//     email: "al-hadid@example.com",
//     contact: "+9662865333",
//     plan: "Basic",
//     date: "2015-12-18",
//   },
//   {
//     name: "Anas",
//     email: "al-hadid@example.com",
//     contact: "+9662865373",
//     plan: "Standard",
//     date: "2016-10-08",
//   },
//   {
//     name: "Zain",
//     email: "al-hadid@example.com",
//     contact: "+9662865923",
//     plan: "Advance",
//     date: "2017-06-15",
//   },
//   {
//     name: "Samia",
//     email: "al-hadid@example.com",
//     contact: "+9662865323",
//     plan: "Basic",
//     date: "2019-01-12",
//   },
//   {
//     name: "Ibrar",
//     email: "al-hadid@example.com",
//     contact: "+9662865223",
//     plan: "Basic",
//     date: "2020-07-21",
//   },
//   {
//     name: "Umair",
//     email: "al-hadid@example.com",
//     contact: "+9662865423",
//     plan: "Standard",
//     date: "2020-07-21",
//   },
//   {
//     name: "Salman",
//     email: "al-hadid@example.com",
//     contact: "+9662865223",
//     plan: "Basic",
//     date: "2020-07-21",
//   },
//   {
//     name: "Bilal",
//     email: "al-hadid@example.com",
//     contact: "+9662865123",
//     plan: "Advance",
//     date: "2020-07-21",
//   },
//   {
//     name: "Sabir",
//     email: "al-hadid@example.com",
//     contact: "+9662865393",
//     plan: "Advance",
//     date: "2020-07-21",
//   },
//   {
//     name: "Zain",
//     email: "al-hadid@example.com",
//     contact: "+9662865383",
//     plan: "Advance",
//     date: "2017-06-15",
//   },
//   {
//     name: "Saleha",
//     email: "al-hadid@example.com",
//     contact: "+9662861233",
//     plan: "Advance",
//     date: "2019-01-12",
//   },
//   {
//     name: "Waqas",
//     email: "al-hadid@example.com",
//     contact: "+9662865343",
//     plan: "Basic",
//     date: "2020-07-21",
//   },
//   {
//     name: "Umair",
//     email: "al-hadid@example.com",
//     contact: "+9662865883",
//     plan: "Standard",
//     date: "2020-07-21",
//   },
// ];

const Customers = () => {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [rowsLength, setRowsLength] = useState([]);
  const { width } = useWindowSize();
  // const [search, setSearch] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  // Handle Search
  const handleSearch = (event) => {
    // setSearch(event.target.value);
    if (event.target.value === "") {
      setRows(allRows);
    } else {
      const filteredRows = rows.filter(
        (row) =>
          row?.fullName
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          row?.email
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          row?.userPlan?.plan?.planName
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          row?.phoneNumber?.includes(event.target.value) ||
          row?.createdAt
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase())
      );
      setRows(filteredRows);
    }
  };

  // Handle Sorting
  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);

    const sortedRows = [...rows].sort((a, b) => {
      if (isAscending) {
        return a[property] > b[property] ? 1 : -1;
      }
      return a[property] < b[property] ? 1 : -1;
    });
    setRows(sortedRows);
  };

  // Handle Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const fetchCustomers = useCallback(async () => {
    const url = `users?order=${order.toUpperCase()}&page=${page + 1}&take=${rowsPerPage}`;
    try {
      const response = await get(url, {}, onLoading);
      if (response?.status === 200 || response?.status === 201) {
        setRowsLength(response.data.meta.itemCount);
        setRows(response.data.data);
        setAllRows(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    } finally {
      setIsLoading(false); // Ensure loading state is updated
    }
  }, [order, page, rowsPerPage]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return (
    <Box sx={{ p: width > 450 ? 3 : 1.3 }}>
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between", // Distribute items to both ends
          p: 2,
          marginBottom: "2%",
        }}
      >
        {/* Left-Aligned Heading */}
        <Box>
          <Typography
            sx={{
              fontFamily: "Tajawal-Bold",
              color: "#0A0A0A",
              fontSize: width > 900 ? "26px" : "18px",
              fontWeight: 700,
              marginRight: "3%",
            }}
          >
            Customers
          </Typography>
        </Box>

        {/* Right-Aligned Search Field */}
        <TextField
          variant="outlined"
          placeholder="Search"
          onChange={handleSearch}
          sx={{
            width: width > 600 ? "270px" : "170px", // Set the specific width
            mb: 1,
            borderRadius: "14px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px", // Apply border-radius to the input field
              height: "55px",
            },
          }}
        />
      </Card>

      {/* Search Bar */}

      {/* Table */}
      <Paper
        sx={{
          overflow: "hidden",
          borderRadius: 2,
          fontFamily: "Tajawal-Regular",
        }}
      >
        <TableContainer sx={{ height: "612px", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "fullName"}
                    direction={orderBy === "fullName" ? order : "asc"}
                    onClick={() => handleSort("fullName")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  Contact
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "userPlan?.plan?.planName"}
                    direction={
                      orderBy === "userPlan?.plan?.planName" ? order : "asc"
                    }
                    onClick={() => handleSort("userPlan?.plan?.planName")}
                  >
                    Plan Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "date"}
                    direction={orderBy === "date" ? order : "asc"}
                    onClick={() => handleSort("date")}
                  >
                    Create Date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow sx={{ height: "500px", width: "100%" }}>
                  <TableCell
                    colSpan={4}
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      py: 2,
                      borderBottom: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        // textAlign: "center",
                        // background: "grey",
                      }}
                    >
                      <CircularLoader />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows &&
                rows.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <strong
                        sx={{ fontFamily: "Tajawal-Bold", fontWeight: 700 }}
                      >
                        {row.fullName}
                      </strong>
                      <br />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Tajawal-Regular",
                          fontWeight: 400,
                          fontSize: "14px",
                        }}
                      >
                        {row.email}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Tajawal-Regular",
                        fontWeight: 400,
                        fontSize: "14px",
                      }}
                    >
                      {row.phoneNumber}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Tajawal-Regular",
                        fontWeight: 400,
                        fontSize: "14px",
                      }}
                    >
                      {row?.userPlan?.plan?.planName}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Tajawal-Regular",
                        fontWeight: 400,
                        fontSize: "14px",
                      }}
                    >
                      {new Date(row.createdAt).toLocaleDateString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow sx={{ height: "500px", width: "100%" }}>
                  <TableCell
                    colSpan={4}
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      py: 2,
                      borderBottom: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        // textAlign: "center",
                        // background: "grey",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontFamily: "Tajawal-Bold",
                            fontWeight: 600,
                            fontSize: "24px",
                          }}
                        >
                          Customer lists is empty.
                        </Typography>
                        <Typography
                          sx={{
                            marginTop: "1%",
                            textAlign: "center",
                            fontFamily: "Tajawal-Bold",
                            fontWeight: 700,
                            fontSize: "22px",
                            color: "#00000099",
                          }}
                        >
                          In the customer lists, you will get the customer
                          personal and his plan information.
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > 0 && (
          <TablePagination
            component="div"
            count={rowsLength}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            // showLastButton={true}
          />
        )}
      </Paper>
    </Box>
  );
};

export default Customers;
